import { FieldModel, UserId } from '@cibo/core'
import { AssignUserControl, AuthUserPermission, Can, useAuth, usePermissions } from '@cibo/profile'
import { LoadingButton } from '@mui/lab'
import { Button, FormControl, FormLabel, InputLabel, Stack } from '@mui/material'
import { Field, FieldProps, Form, Formik } from 'formik'
import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { number, object, string } from 'yup'
import {
  LabelEditorField,
  NameEditorField,
  fieldNameValidationSchemaByUserId,
  fieldNameValidationSchemaWithOwnerField,
} from '..'
import { AssignAndCreateFarmField } from '../LandManagementProvider/hooks/useAssignFarmDialog/AssignFarmForm'

interface FieldEditorFormProps {
  acres?: number
  errorMessages?: string[]
  fieldTouched: boolean
  initialValues: {
    name: string
    owner?: UserId
  }
  onCancel: () => void
  onSubmit: (values: any) => void
  setIsCreatingFieldset?: Dispatch<SetStateAction<boolean>>
  isCreatingFieldset?: boolean
  fieldset?: FieldModel[]
  setSensingDialogOpen?: (open: boolean) => void
  alertComponents?: ReactNode
}

export const FieldEditorForm = ({
  fieldTouched,
  initialValues,
  onCancel,
  onSubmit,
  setIsCreatingFieldset,
  fieldset,
  setSensingDialogOpen,
  alertComponents,
}: FieldEditorFormProps) => {
  const { persona, userId } = useAuth()
  const { can } = usePermissions()
  const { t } = useTranslation('FieldEditorForm')
  const [fullText, setFullText] = useState<string>()

  const inputLabelProps = useMemo(() => ({ shrink: true }), [])
  const textFieldProps = useMemo(() => ({ helperText: t('willNotNotify') }), [])
  // if there are no fields, then we are probably creating a new field in which case, we only care about the user permission
  const canEditOwnerOnAllFields = !!fieldset
    ? fieldset.every(field => field.can(['assign_owner']))
    : true
  const canEditOwner =
    can({
      useAny: [AuthUserPermission.FIELDS_OWNER_MODIFY, AuthUserPermission.FIELDS_OWNER_ASSIGN],
    }) && canEditOwnerOnAllFields

  const validationSchema = useMemo(
    () =>
      object().shape({
        name: canEditOwner
          ? fieldNameValidationSchemaWithOwnerField({ fieldName: 'name', t })
          : fieldNameValidationSchemaByUserId({
              fieldName: 'name',
              t,
              userId,
            }),
        description: string().max(2048, t('max', { context: 'description' })),
        owner: number()[canEditOwner ? 'required' : 'optional'](
          t('required', { context: 'owner' })
        ),
      }),
    [t, persona]
  )

  return (
    <Formik
      initialValues={initialValues}
      validateOnMount
      validateOnChange
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ isValid, isSubmitting, values, resetForm, submitForm, errors }) => {
        const fieldCannotBeCreated = !isValid || !fieldTouched
        return (
          <Form style={{ flex: 1 }}>
            <Field type="hidden" name="parentId" />
            <Stack spacing={2} sx={{ minHeight: '100%' }}>
              <Stack spacing={2}>
                <FormControl required>
                  <FormLabel>{t('name')}</FormLabel>
                  <NameEditorField name="name" />
                </FormControl>

                <Can
                  useAny={[
                    AuthUserPermission.FIELDS_OWNER_ASSIGN,
                    AuthUserPermission.FIELDS_OWNER_MODIFY,
                  ]}
                >
                  <Field name="owner">
                    {({ form: { values, setFieldValue } }: FieldProps) => (
                      <AssignUserControl
                        setFullText={setFullText}
                        fullText={fullText}
                        userId={values.owner}
                        setUserId={(value?: number) => setFieldValue('owner', value)}
                        inputLabelProps={inputLabelProps}
                        textFieldProps={textFieldProps}
                        disabled={!canEditOwner}
                        required
                      />
                    )}
                  </Field>
                </Can>
                <Can useAny={[AuthUserPermission.FARMS_MANAGE]}>
                  <Stack>
                    <InputLabel shrink htmlFor="metadata.termsets[0]">
                      {t('farm')}
                    </InputLabel>
                    <AssignAndCreateFarmField
                      data-testid="farmNameDropdown"
                      id="metadata.termsets[0]"
                      name="metadata.termsets[0]"
                      sx={{ minWidth: 'unset' }}
                      ownerId={values.owner}
                    />
                  </Stack>
                </Can>
                <Stack>
                  <InputLabel shrink htmlFor="metadata.labels">
                    {t('labels')}
                  </InputLabel>
                  <Field name="metadata.labels">
                    {/* @ts-ignore */}
                    {({ form: { values, setFieldValue } }) => (
                      <LabelEditorField
                        labels={values.metadata?.labels}
                        onChange={labels => setFieldValue('metadata.labels', labels)}
                      />
                    )}
                  </Field>
                </Stack>

                {alertComponents}
              </Stack>

              <Stack spacing={2}>
                <Stack direction="row" spacing={2}>
                  <Button
                    onClick={onCancel}
                    variant="outlined"
                    data-testid="cancelButton"
                    fullWidth
                  >
                    {t('cancel')}
                  </Button>
                  {!fieldset && (
                    <LoadingButton
                      onClick={() => submitForm()}
                      disabled={fieldCannotBeCreated}
                      loading={isSubmitting}
                      variant="contained"
                      data-testid="saveButton"
                      fullWidth
                    >
                      {t('create')}
                    </LoadingButton>
                  )}
                </Stack>
                <LoadingButton
                  onClick={() => {
                    setIsCreatingFieldset && setIsCreatingFieldset(true)
                    submitForm().then(() => {
                      resetForm({ values: { ...values, name: '' } })
                    })
                  }}
                  disabled={fieldCannotBeCreated}
                  loading={isSubmitting}
                  variant="contained"
                  data-testid="saveAndCreateButton"
                  fullWidth
                >
                  {t('saveAndCreateAnother')}
                </LoadingButton>
                {fieldset && fieldset.length > 0 && (
                  <Button
                    onClick={() => {
                      if (fieldTouched && isValid) {
                        submitForm().then(() => setSensingDialogOpen && setSensingDialogOpen(true))
                      } else {
                        onSubmit({})
                      }
                    }}
                    disabled={fieldTouched && !isValid}
                    variant="outlined"
                    color="secondary"
                    fullWidth
                  >
                    {t(fieldTouched ? 'confirmInfoSave' : 'confirmInfo', {
                      count: fieldset.length + (!!fieldTouched && !!isValid ? 1 : 0),
                    })}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Form>
        )
      }}
    </Formik>
  )
}
